import type { HorizonRuleSnapshot, Id } from '../domain/model';
import type { MealTemplate } from '../domain/meals';

export interface FoodCategory {
  id: Id;
  label: string;
  icon: string;
}

export const FOOD_TAXONOMY_VERSION = 'food-taxonomy-fr-v1';

export const FOOD_FAMILIES: Record<string, string[]> = {
  meat: ['Bœuf', 'Porc', 'Volaille', 'Agneau', 'Charcuterie'],
  fish: ['Poisson blanc', 'Poisson gras', 'Crustacés', 'Coquillages'],
  vegetables: ['Légumes', 'Herbes', 'Soupe', 'Mélange'],
  fruit: ['Fruits rouges', 'Fruits à noyau', 'Agrumes', 'Compote'],
  prepared: ['Plat maison', 'Plat préparé', 'Sauce', 'Bouillon'],
  bread: ['Pain', 'Viennoiserie', 'Pâtisserie', 'Pâte'],
  dessert: ['Glace', 'Sorbet', 'Gâteau', 'Dessert'],
  dairy: ['Fromage', 'Beurre', 'Crème', 'Lait'],
  other: ['Autre aliment'],
};

export const FOOD_DETAILS: Record<string, string[]> = {
  Bœuf: ['Bourguignon', 'Steak', 'Haché', 'Rôti'],
  Volaille: ['Poulet', 'Dinde', 'Canard'],
  'Poisson blanc': ['Cabillaud', 'Colin', 'Merlu'],
  'Poisson gras': ['Saumon', 'Thon', 'Maquereau'],
  Légumes: ['Courgette', 'Carotte', 'Haricot', 'Épinard'],
  'Plat maison': ['Gratin', 'Lasagnes', 'Ragoût', 'Quiche'],
  Pain: ['Baguette', 'Pain de mie', 'Pain complet'],
};

export const FOOD_CATEGORIES: FoodCategory[] = [
  { id: 'meat', label: 'Viandes', icon: '🥩' },
  { id: 'fish', label: 'Poissons', icon: '🐟' },
  { id: 'vegetables', label: 'Légumes', icon: '🥕' },
  { id: 'fruit', label: 'Fruits', icon: '🍓' },
  { id: 'prepared', label: 'Plats cuisinés', icon: '🍲' },
  { id: 'bread', label: 'Pains et pâtisseries', icon: '🥖' },
  { id: 'dessert', label: 'Glaces et desserts', icon: '🍨' },
  { id: 'dairy', label: 'Produits laitiers', icon: '🧀' },
  { id: 'other', label: 'Autre', icon: '❄️' },
];

const localRule = (durationDays: number | null, label: string): HorizonRuleSnapshot => ({
  id: durationDays == null ? 'rotation-none' : `rotation-${durationDays}`,
  label,
  durationDays,
  sourceTitle: 'Choix d’organisation du foyer',
  sourceUrl: null,
  sourceCheckedOn: '2026-07-23',
  version: 'rotation-locale-v1',
  disclaimer:
    'Cette durée organise la rotation et la qualité. Elle ne permet pas de déterminer si un aliment est sans danger.',
});

export const HORIZON_RULES: HorizonRuleSnapshot[] = [
  localRule(null, 'Sans horizon automatique'),
  localRule(30, 'Rotation à 1 mois'),
  localRule(90, 'Rotation à 3 mois'),
  localRule(180, 'Rotation à 6 mois'),
  localRule(365, 'Rotation à 12 mois'),
];

export const FOOD_STATES = ['Cru', 'Cuit', 'Cuisiné'] as const;
export type FoodState = (typeof FOOD_STATES)[number] | '';

export const suggestRotationRuleId = (
  categoryId: string,
  foodState: FoodState,
): string => {
  if (categoryId === 'vegetables' || categoryId === 'fruit') return 'rotation-180';
  if (categoryId === 'bread' || categoryId === 'dessert' || categoryId === 'dairy') {
    return 'rotation-90';
  }
  if (categoryId === 'meat' || categoryId === 'fish') {
    return foodState === 'Cru' ? 'rotation-180' : 'rotation-90';
  }
  if (categoryId === 'prepared') return 'rotation-90';
  return 'rotation-none';
};

export const MEAL_TEMPLATES: MealTemplate[] = [
  {
    id: 'soup',
    title: 'Soupe ou velouté',
    description: 'Une base souple pour utiliser légumes, herbes ou restes de plat.',
    acceptedCategoryIds: ['vegetables', 'prepared'],
    requiredPantryIngredients: ['bouillon', 'oignon'],
    optionalIngredients: ['crème', 'épices', 'pain'],
    tags: ['végétarien', 'soupe'],
    defaultPortions: 2,
    stockIngredients: [
      { id: 'soup-vegetables', label: 'Légumes ou restes', categoryIds: ['vegetables', 'prepared'], nameTerms: ['légume', 'soupe', 'reste'], required: true, quantityPerPortion: 180, unit: 'g' },
    ],
    version: 'meals-v2',
  },
  {
    id: 'gratin',
    title: 'Gratin anti-gaspi',
    description: 'Assembler une base congelée avec une liaison et un dessus croustillant.',
    acceptedCategoryIds: ['vegetables', 'prepared', 'meat', 'fish'],
    requiredPantryIngredients: ['œuf ou crème', 'fromage ou chapelure'],
    optionalIngredients: ['herbes', 'moutarde'],
    tags: ['gratin', 'four'],
    defaultPortions: 2,
    stockIngredients: [
      { id: 'gratin-base', label: 'Garniture', categoryIds: ['vegetables', 'prepared', 'meat', 'fish'], nameTerms: ['reste'], required: true, quantityPerPortion: 180, unit: 'g' },
      { id: 'gratin-dairy', label: 'Crème ou fromage', categoryIds: ['dairy'], nameTerms: ['crème', 'fromage'], required: false, quantityPerPortion: 40, unit: 'g' },
    ],
    version: 'meals-v2',
  },
  {
    id: 'pan',
    title: 'Poêlée express',
    description: 'Réchauffer une ou plusieurs portions et compléter avec un féculent.',
    acceptedCategoryIds: ['vegetables', 'meat', 'fish', 'prepared', 'sauce'],
    requiredPantryIngredients: ['huile', 'féculent au choix'],
    optionalIngredients: ['ail', 'épices'],
    tags: ['poêlée', 'rapide'],
    defaultPortions: 2,
    stockIngredients: [
      { id: 'pan-base', label: 'Base à poêler', categoryIds: ['vegetables', 'prepared'], nameTerms: ['poêlée'], required: true, quantityPerPortion: 150, unit: 'g' },
      { id: 'pan-protein', label: 'Viande ou poisson', categoryIds: ['meat', 'fish'], nameTerms: [], required: false, quantityPerPortion: 100, unit: 'g' },
    ],
    version: 'meals-v2',
  },
  {
    id: 'pasta',
    title: 'Pâtes ou riz en sauce',
    description: 'Utiliser une sauce, une viande ou des légumes comme garniture.',
    acceptedCategoryIds: ['sauce', 'meat', 'vegetables', 'prepared'],
    requiredPantryIngredients: ['pâtes ou riz'],
    optionalIngredients: ['fromage', 'herbes'],
    tags: ['pâtes', 'riz'],
    defaultPortions: 2,
    stockIngredients: [
      { id: 'pasta-sauce', label: 'Sauce ou garniture', categoryIds: ['prepared', 'meat', 'vegetables'], nameTerms: ['sauce'], required: true, quantityPerPortion: 120, unit: 'g' },
    ],
    version: 'meals-v2',
  },
  {
    id: 'tart',
    title: 'Tarte ou chaussons',
    description: 'Transformer une petite quantité restante en garniture.',
    acceptedCategoryIds: ['vegetables', 'meat', 'fish', 'prepared', 'dairy'],
    requiredPantryIngredients: ['pâte', 'œuf'],
    optionalIngredients: ['fromage', 'salade'],
    tags: ['tarte', 'œuf'],
    defaultPortions: 4,
    stockIngredients: [
      { id: 'tart-filling', label: 'Garniture', categoryIds: ['vegetables', 'meat', 'fish', 'prepared'], nameTerms: ['reste'], required: true, quantityPerPortion: 100, unit: 'g' },
      { id: 'tart-dairy', label: 'Produit laitier', categoryIds: ['dairy'], nameTerms: ['crème', 'fromage'], required: false, quantityPerPortion: 25, unit: 'g' },
    ],
    version: 'meals-v2',
  },
  {
    id: 'dessert',
    title: 'Dessert aux fruits',
    description: 'Employer les fruits congelés en compote, crumble ou smoothie.',
    acceptedCategoryIds: ['fruit', 'dessert'],
    requiredPantryIngredients: ['sucre facultatif'],
    optionalIngredients: ['yaourt', 'farine', 'flocons'],
    tags: ['dessert', 'fruit'],
    defaultPortions: 2,
    stockIngredients: [
      { id: 'dessert-fruit', label: 'Fruits', categoryIds: ['fruit'], nameTerms: ['fruit', 'compote'], required: true, quantityPerPortion: 150, unit: 'g' },
      { id: 'dessert-dairy', label: 'Yaourt ou crème', categoryIds: ['dairy'], nameTerms: ['yaourt', 'crème'], required: false, quantityPerPortion: 80, unit: 'g' },
    ],
    version: 'meals-v2',
  },
];

export const OFFICIAL_SAFETY_SOURCES = [
  {
    title: 'Ministère de l’Agriculture — bonnes pratiques de congélation',
    url: 'https://agriculture.gouv.fr/les-bonnes-pratiques-de-la-congelation',
  },
  {
    title: 'Anses — conserver les aliments et chaîne du froid',
    url: 'https://www.anses.fr/fr/content/comment-bien-conserver-ses-aliments-et-ne-pas-interrompre-la-cha%C3%AEne-du-froid',
  },
  {
    title: 'DGCCRF — DLC et DDM',
    url: 'https://www.economie.gouv.fr/dgccrf/les-fiches-pratiques/date-limite-de-consommation-et-date-de-durabilite-minimale-ce-que-vous-devez-savoir',
  },
];

export const categoryLabel = (categoryId: string): string =>
  FOOD_CATEGORIES.find((category) => category.id === categoryId)?.label ??
  (categoryId === 'sauce' ? 'Sauces et bases' : 'Autre');
